import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Camera, Check, Clock, Loader2, LogOut, Share2, X } from "lucide-react";
import { API } from "@/lib/api";

// LIFTR BINGO for members on the web (backend routes/bingo.py): the same sign-in as the member app (gym,
// phone, code by email) and the same card. Members without the app play here. The member's token is kept
// in this browser and sent as a bearer header, never as the staff cookie.

const TOKEN_KEY = "liftr_member_token";
const member = axios.create({ baseURL: API, withCredentials: false });
// ?preview=1: a facility head or coach sees their gym's card with their FPA login (staff cookie), nothing saved
const staff = axios.create({ baseURL: API, withCredentials: true });
const PREVIEW = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
const auth = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
member.interceptors.request.use((cfg) => {
  const t = auth();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const err = (e, fb) => e?.response?.data?.detail || fb;
const input = "w-full rounded-full bg-white/10 border border-white/25 px-4 py-3 text-base text-white placeholder:text-white/50 focus:border-[#e8b84c] focus:outline-none";
const gold = "#e2b654";
const red = "#bc0e18";
// The BINGO teaser's look: Anton for headlines, Montserrat for everything else, gold letter-spaced caps
const FONTS = "https://fonts.googleapis.com/css2?family=Anton&family=Montserrat:wght@500;600;700&display=swap";
const head = { fontFamily: "Anton, Impact, sans-serif", letterSpacing: "0.01em" };
const caps = { fontFamily: "Montserrat, sans-serif", fontWeight: 700, letterSpacing: "0.3em", color: gold };
const pill = "w-full rounded-full py-3 border-2 text-sm uppercase disabled:opacity-50";
const fmt = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

function SignIn({ onDone }) {
  const [gyms, setGyms] = useState([]);
  const [gym, setGym] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { member.get("/app/gyms").then((r) => setGyms(r.data.gyms || [])).catch(() => {}); }, []);
  const ask = async () => {
    setBusy(true); setMsg("");
    try { const r = await member.post("/app/auth/otp/request", { facility_id: gym, phone }); setSent(r.data.sent_to); }
    catch (e) { setMsg(err(e, "We couldn't send your code. Try again.")); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await member.post("/app/auth/otp/verify", { facility_id: gym, phone, code });
      try { localStorage.setItem(TOKEN_KEY, r.data.token); } catch { /* private mode: this visit only */ }
      member.defaults.headers.common.Authorization = `Bearer ${r.data.token}`;
      onDone();
    } catch (e) { setMsg(err(e, "That code didn't work.")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <p className="text-white/85 text-center text-sm">Sign in with the number you gave the gym. We'll email you a code.</p>
      {!sent ? (
        <>
          <select value={gym} onChange={(e) => setGym(e.target.value)} className={input} data-testid="bingo-gym">
            <option value="" className="text-black">Pick your gym</option>
            {gyms.map((g) => <option key={g.id} value={g.id} className="text-black">{g.name}</option>)}
          </select>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="Mobile number" className={input} data-testid="bingo-phone" />
          <button type="button" disabled={!gym || phone.replace(/\D/g, "").length < 8 || busy} onClick={ask}
            className={pill} style={{ ...caps, borderColor: gold }}>
            {busy ? "Sending" : "Send my code"}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-white/70 text-center">Code sent to {sent}.</p>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="6-digit code" className={input} data-testid="bingo-code" />
          <button type="button" disabled={code.length < 4 || busy} onClick={verify}
            className={pill} style={{ ...caps, borderColor: gold }}>
            {busy ? "Checking" : "Sign in"}
          </button>
          <button type="button" onClick={() => { setSent(null); setCode(""); }} className="w-full text-sm text-white/60">Use another number</button>
        </>
      )}
      {msg && <p className="text-sm text-center text-amber-200">{msg}</p>}
    </div>
  );
}

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

function Square({ cell, n, onOpen }) {
  const pending = cell.claim?.status === "pending";
  return (
    <button type="button" onClick={() => onOpen(cell)} data-testid={`bingo-cell-${cell.id}`}
      className="relative flex flex-col justify-start rounded-lg p-1.5 pb-3.5 sm:p-2 sm:pb-4 text-left min-h-[104px] sm:min-h-[128px] shadow-[3px_4px_0_rgba(110,6,14,0.9)]"
      style={{ background: "#faf6ee", color: "#1e1e1e" }}>
      <div className="flex items-start justify-between">
        <span className="text-lg sm:text-2xl leading-none" style={{ ...head, color: red }}>{String(n).padStart(2, "0")}</span>
        {cell.done && (
          <span className="h-6 w-6 sm:h-8 sm:w-8 rounded-full flex items-center justify-center" style={{ background: gold, boxShadow: "inset 0 0 0 2px #faf6ee, inset 0 0 0 3px #1e1e1e" }}>
            <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3.5} />
          </span>
        )}
        {!cell.done && pending && <Clock className="h-4 w-4" style={{ color: red }} />}
      </div>
      <div className="h-px bg-[#ded6c6] my-1" />
      <div className="text-[9.5px] sm:text-[11.5px] leading-tight" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}>{cell.title}</div>
      {!cell.done && !pending && cell.progress && (
        <span className="absolute bottom-1.5 left-1.5 right-1.5 h-1 rounded bg-black/10">
          <span className="block h-1 rounded" style={{ background: red, width: `${Math.min(100, (cell.progress.value / cell.progress.target) * 100)}%` }} />
        </span>
      )}
    </button>
  );
}

const Sparkle = ({ className, color = gold, size = 18 }) => (
  <svg viewBox="0 0 20 20" width={size} height={size} className={`absolute pointer-events-none ${className}`} aria-hidden="true">
    <path d="M10 0 L12.2 7.8 L20 10 L12.2 12.2 L10 20 L7.8 12.2 L0 10 L7.8 7.8 Z" fill={color} />
  </svg>
);

function Detail({ cell, onClose, onSent, preview }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async (file) => {
    if (!file) return;
    setBusy(true); setMsg("");
    try {
      await member.post("/app/bingo/proof", { task_id: cell.id, image: await readFile(file) });
      onSent();
    } catch (e) { setMsg(err(e, "That photo didn't send. Try again.")); } finally { setBusy(false); }
  };
  const p = cell.progress;
  const claim = cell.claim;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl text-white p-5 space-y-3 border" style={{ background: "#141414", borderColor: gold, fontFamily: "Montserrat, sans-serif" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <h3 className="flex-1 text-2xl uppercase" style={head}>{cell.title}</h3>
          <button type="button" onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        {cell.done ? <p className="text-emerald-300">Done {cell.done_at ? new Date(cell.done_at).toLocaleDateString() : ""}. Nice work.</p> : (
          <>
            {p && <p className="text-white/80">{fmt(p.value)} of {fmt(p.target)} {p.unit}</p>}
            {cell.how && <p className="text-sm text-white/70">{cell.how}</p>}
            {cell.proof === "gym" && <p className="text-sm text-white/70">Ticks itself when you check in at the gym.</p>}
            {cell.proof === "data" && !cell.photo_ok && <p className="text-sm text-white/70">Ticks itself from your phone's health data in the LIFTR app.</p>}
            {claim?.status === "pending" && <p className="text-sm text-amber-200">Photo sent. The team will check it soon.</p>}
            {claim?.status === "rejected" && <p className="text-sm text-amber-200">Your last photo wasn't accepted{claim.note ? `: ${claim.note}` : ""}. Send another.</p>}
            {preview && cell.photo_ok && <p className="text-sm text-amber-200">Preview: members send their photo here, and you check it in FPA.</p>}
            {!preview && cell.photo_ok && claim?.status !== "pending" && (
              <label className={`block cursor-pointer text-center ${pill}`} style={{ ...caps, borderColor: gold }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : <><Camera className="h-4 w-4 inline mr-1.5" />Send a photo</>}
                <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => send(e.target.files?.[0])} />
              </label>
            )}
          </>
        )}
        {msg && <p className="text-sm text-amber-200">{msg}</p>}
      </div>
    </div>
  );
}

export default function MemberBingo() {
  const [signedIn, setSignedIn] = useState(PREVIEW || !!auth());
  const [card, setCard] = useState(null);
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    if (PREVIEW) {
      staff.get("/bingo/preview").then((r) => setCard(r.data)).catch((e) => setMsg(err(e, "Sign in to FPA first, then open this again.")));
      return;
    }
    member.get("/app/bingo").then((r) => setCard(r.data)).catch((e) => {
      if (e?.response?.status === 401) { try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } setSignedIn(false); }
      else setMsg(err(e, "Couldn't load your card."));
    });
  }, []);
  useEffect(() => { if (signedIn) load(); }, [signedIn, load]);
  useEffect(() => { document.title = "LIFTR BINGO"; }, []);

  const status = useMemo(() => {
    const m = card?.milestones || {};
    return m.full_house ? "Full house." : m.line ? "One full line." : m.early_five ? "Early five." : null;
  }, [card]);

  const share = async () => {
    const url = `${API.replace(/\/api$/, "")}${card.share_url}`;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "liftr-bingo.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "LIFTR BINGO" });
        return;
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "liftr-bingo.png";
      a.click();
    } catch { /* share sheet closed */ }
  };
  const signOut = () => {
    member.post("/app/auth/logout").catch(() => {});
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setCard(null); setSignedIn(false);
  };

  return (
    <div className="min-h-screen px-3 py-6" style={{ background: red, fontFamily: "Montserrat, sans-serif" }}>
      <link rel="stylesheet" href={FONTS} />
      <div className="relative mx-auto max-w-xl rounded-[28px] border p-4 sm:p-6 space-y-5" style={{ borderColor: gold }}>
        <Sparkle className="left-5 top-14" size={22} />
        <Sparkle className="right-6 top-8" color="#fff" size={14} />
        <div className="text-center space-y-3 pt-2">
          <img src="/brand/liftr-wordmark.png" alt="LIFTR" className="mx-auto h-8 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
          <div className="text-xs sm:text-sm" style={caps}>NOVEMBER 2026</div>
        </div>
        {!signedIn ? (
          <>
            <div className="text-center text-white uppercase leading-[0.95] text-5xl sm:text-6xl" style={head}>
              Five letters.<br />Twenty squares.<br /><span style={{ color: gold }}>One full house.</span>
            </div>
            <SignIn onDone={() => setSignedIn(true)} />
          </>
        ) : !card ? (
          <div className="text-center text-white/80">{msg || <Loader2 className="h-5 w-5 animate-spin inline" />}</div>
        ) : (
          <>
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2 text-center text-white text-6xl sm:text-7xl leading-none" style={head}>
              {card.letters.split("").map((l) => <div key={l}>{l}</div>)}
            </div>
            {card.subtitle && (
              <div className="text-center -mt-2 space-y-1">
                <div className="text-white text-2xl sm:text-3xl uppercase" style={head}>{card.subtitle}</div>
                {card.tagline && <div className="text-xs text-white/85">{card.tagline}</div>}
              </div>
            )}
            <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
              {card.rows.flat().map((c, i) => <Square key={c.id} cell={c} n={i + 1} onOpen={setOpen} />)}
            </div>
            <div className="text-center text-white uppercase leading-[0.95] pt-1" style={head}>
              <div className="text-5xl sm:text-6xl">{card.done_count} of {card.total} squares.</div>
              <div className="text-5xl sm:text-6xl" style={{ color: gold }}>{status || "Game on."}</div>
            </div>
            {!card.started && <p className="text-sm text-white/85 text-center">BINGO starts on 1 November. Squares tick from then on.</p>}
            {card.share_url && (
              <button type="button" onClick={share} className={`${pill} inline-flex items-center justify-center gap-2`} style={{ ...caps, borderColor: gold }}>
                <Share2 className="h-4 w-4" />Share my card
              </button>
            )}
            <div className="rounded-2xl p-4 text-sm text-white/90 space-y-1.5" style={{ background: "rgba(0,0,0,0.18)" }}>
              {Object.values(card.prizes || {}).map((p) => <div key={p}>{p}</div>)}
            </div>
            {card.preview ? <p className="text-xs text-center text-white/70">{card.name}. This is what members at your gym see. Nothing here is saved.</p>
              : <button type="button" onClick={signOut} className="w-full text-xs text-white/60 inline-flex items-center justify-center gap-1"><LogOut className="h-3 w-3" />Sign out ({card.name})</button>}
          </>
        )}
      </div>
      {open && <Detail cell={open} preview={!!card?.preview} onClose={() => setOpen(null)} onSent={() => { setOpen(null); load(); }} />}
    </div>
  );
}
